import React, { useState, useEffect } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Button,
  Paper,
  Popover,
  Switch,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import dayjs from 'dayjs';

const DateRangePicker = ({ start, end, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [rangeMode, setRangeMode] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [rangeStart, setRangeStart] = useState(start ? new Date(start) : null);
  const [rangeEnd, setRangeEnd] = useState(end ? new Date(end) : null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    if (onChange && typeof onChange === 'function') {
      if (rangeStart && rangeEnd) {
        const formattedRange = {
          startDate: rangeStart ? dayjs(rangeStart).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null,
          endDate: rangeEnd ? dayjs(rangeEnd).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null,
        };
        onChange(formattedRange);
      } else if (selectedDate) {
        onChange({
          startDate: selectedDate ? dayjs(selectedDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null,
          endDate: null,
        });
      }
    }
  }, [rangeStart, rangeEnd, selectedDate]);


  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRangeModeToggle = () => {
    setRangeMode(!rangeMode);
    setRangeStart(null);
    setRangeEnd(null);
    setSelectedDate(null);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();

    const days = [];
    for (let i = 0; i < firstDay; i++) {
      const prevDate = new Date(year, month, -i);
      days.unshift({
        date: prevDate,
        disabled: true,
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        disabled: false,
      });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        disabled: true,
      });
    }

    return days;
  };

  const handleDateClick = (date) => {
    if (rangeMode) {
      if (!rangeStart || (rangeStart && rangeEnd)) {
        setRangeStart(date);
        setRangeEnd(null);
      } else {
        if (date < rangeStart) {
          setRangeEnd(rangeStart);
          setRangeStart(date);
        } else {
          setRangeEnd(date);
        }
      }
    } else {
      setSelectedDate(date);
    }
  };

  const isDateInRange = (date) => {
    if (!rangeStart || !rangeEnd) return false;
    return date >= rangeStart && date <= rangeEnd;
  };

  const isDateSelected = (date) => {
    if (rangeMode) {
      return (
        date.getTime() === (rangeStart?.getTime() ?? null) ||
        date.getTime() === (rangeEnd?.getTime() ?? null)
      );
    }
    return date.getTime() === (selectedDate?.getTime() ?? null);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const open = Boolean(anchorEl);
  const days = getDaysInMonth(currentMonth);

  return (
    <div>
      <Button
        variant="outlined"
        endIcon={<KeyboardArrowDownIcon />}
        onClick={handleClick}
        sx={{
          borderColor: 'divider',
          textTransform: 'none',
          fontStyle: 'Outfit',
          py: 1.5,
          borderRadius: '7px',
          fontSize: '14px',
          color: "#848694",
          '&:hover': {
            borderColor: 'primary.main',
          },
        }}
      >
        Dates Completed
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Paper sx={{ p: 2, width: 320 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography>Select dates in range format</Typography>
            <Switch
              checked={rangeMode}
              onChange={handleRangeModeToggle}
              color="primary"
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <IconButton onClick={handlePrevMonth}>
              <KeyboardArrowLeftIcon />
            </IconButton>
            <Typography>
              {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
            </Typography>
            <IconButton onClick={handleNextMonth}>
              <KeyboardArrowRightIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day) => (
              <Typography
                key={day}
                sx={{
                  textAlign: 'center',
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  mb: 1,
                }}
              >
                {day}
              </Typography>
            ))}
            {days.map((day, index) => (
              <Button
                key={index}
                onClick={() => !day.disabled && handleDateClick(day.date)}
                sx={{
                  minWidth: 0,
                  p: 1,
                  borderRadius: '4px',
                  color: day.disabled ? 'text.disabled' : 'text.primary',
                  backgroundColor: isDateSelected(day.date)
                    ? 'primary.main'
                    : isDateInRange(day.date)
                      ? 'primary.light'
                      : 'transparent',
                  '&:hover': {
                    backgroundColor: isDateSelected(day.date)
                      ? 'primary.dark'
                      : 'action.hover',
                  },
                  '& .MuiTypography-root': {
                    color: isDateSelected(day.date) ? 'white' : 'inherit',
                  },
                }}
                disabled={day.disabled}
              >
                <Typography>{day.date.getDate()}</Typography>
              </Button>
            ))}
          </Box>
        </Paper>
      </Popover>
    </div>
  );
};

export default DateRangePicker;
